import type { CSSProperties } from 'react';
import type { SliceMetric } from '@death-star/shared';
import type { WindowKey } from '../../routes/slicing.js';
import { isNoBaselineMetric } from './format.js';

const WINDOWS: WindowKey[] = ['7d', '30d', '90d', 'since-2026-01-01'];

const WINDOW_LABELS: Record<WindowKey, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  'since-2026-01-01': 'Since 2026-01-01',
};

const ALL_REPOS = '__all__';

// Picker styles match weekly-digest.tsx / velocity-scorecard.tsx so the chrome
// is identical across pages. Chevron rendered as a sibling element because
// background-image SVG chevrons are too faint at 12px on macOS.
const inputStyle: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)',
  height: '36px',
  boxSizing: 'border-box',
  fontFamily: 'var(--loop-font-family-body)',
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  cursor: 'pointer',
  paddingRight: '32px',
};

const selectWrapStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
};

const selectChevronStyle: CSSProperties = {
  position: 'absolute',
  right: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  color: 'var(--loop-neutral-900)',
  display: 'flex',
  alignItems: 'center',
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  marginBottom: 4,
  color: 'var(--loop-neutral-600)',
  fontSize: '0.75rem',
};

const legendSwatch = (color: string): CSSProperties => ({
  display: 'inline-block',
  width: 9,
  height: 9,
  background: color,
  verticalAlign: 'middle',
  marginRight: 4,
  borderRadius: 2,
});

const Chevron = () => (
  <span style={selectChevronStyle} aria-hidden="true">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  </span>
);

export interface FilterBarProps {
  org: string;
  orgs: string[];
  repos: string[];
  selectedRepos: string[];
  window: WindowKey;
  metric: SliceMetric;
  onOrgChange: (org: string) => void;
  onReposChange: (repos: string[] | null) => void;
  onWindowChange: (w: WindowKey) => void;
}

export function FilterBar({ org, orgs, repos, selectedRepos, window: win, metric, onOrgChange, onReposChange, onWindowChange }: FilterBarProps) {
  // The bar chart hides the baseline series for no-baseline metrics
  // (auto_approval, override_rate). Keep the legend in lock-step so we don't
  // advertise a "baseline" swatch the chart never draws.
  const showBaseline = !isNoBaselineMetric(metric);
  const repoValue =
    selectedRepos.length === repos.length
      ? ALL_REPOS
      : selectedRepos[0] ?? ALL_REPOS;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
      <div>
        <label htmlFor="slicing-org-select" style={fieldLabelStyle}>Org</label>
        <div style={selectWrapStyle}>
          <select
            id="slicing-org-select"
            value={org}
            onChange={(e) => onOrgChange(e.target.value)}
            style={selectStyle}
          >
            <option value="">All orgs</option>
            {orgs.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <Chevron />
        </div>
      </div>

      {org !== '' && (
        <div>
          <label htmlFor="slicing-repo-select" style={fieldLabelStyle}>Repo</label>
          <div style={selectWrapStyle}>
            <select
              id="slicing-repo-select"
              value={repoValue}
              onChange={(e) => {
                const v = e.target.value;
                if (v === ALL_REPOS) onReposChange(null);
                else onReposChange([v]);
              }}
              style={selectStyle}
            >
              <option value={ALL_REPOS}>All repos in org</option>
              {repos.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <Chevron />
          </div>
        </div>
      )}

      <div>
        <label htmlFor="slicing-window-select" style={fieldLabelStyle}>Window</label>
        <div style={selectWrapStyle}>
          <select
            id="slicing-window-select"
            value={win}
            onChange={(e) => onWindowChange(e.target.value as WindowKey)}
            style={selectStyle}
          >
            {WINDOWS.map((w) => <option key={w} value={w}>{WINDOW_LABELS[w]}</option>)}
          </select>
          <Chevron />
        </div>
      </div>

      <div style={{ flex: 1 }} />

      <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-700)', paddingBottom: 10, whiteSpace: 'nowrap' }}>
        {showBaseline && (
          <span style={{ marginRight: 8 }}><span style={legendSwatch('var(--loop-neutral-500)')} />baseline</span>
        )}
        <span><span style={legendSwatch('var(--loop-brand-600)')} />current</span>
      </div>
    </div>
  );
}
